import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, TrendingUp, Users, Clock, ArrowRight } from 'lucide-react';
import Seo from '../components/Seo';

const SuccessStoriesPage = () => {
  const caseStudies = [
    {
      hospital: 'An 18 Hospital System in Indiana',
      location: 'Indianapolis, IN',
      image: './media/images/case_Study_1.png',
      challenge: 'Facing chronic nurse staffing gaps across six hospitals, IU Health struggled to balance workloads and relied heavily on costly travel nurses. The system needed a coordinated approach to allocate staff regionally in real time.',
      solution: 'Partnered with DeltaOps to pilot the “Delta Force,” a regional AI-powered float pool that predicted staffing demand, optimized nurse deployment, and synchronized communication across sites.',
      results: [
        { metric: '17%', description: 'Reduction in understaffing' },
        { metric: '$6M+', description: 'Total annual savings across six hospitals' },
        { metric: '75,000 hrs', description: 'Reduction in travel nurse hours' },
        { metric: '8× ROI', description: 'Verified through operational data' },
      ],
      testimonial: {
        quote: "We now redeploy nurses proactively, not reactively. DeltaOps gave us visibility and control we never had before.",
        author: 'Associate Chief Nurse Executive'
      },
      case_id: 'DO-WX01',
    },
    {
      hospital: "An Academic Medical Center",
      location: 'Honolulu, HI',
      image: './media/images/Case_Study_2.png',
      challenge: 'Despite strong daily flow huddles and coordination practices, the medical center still faced hidden bottlenecks caused by manual communication, siloed systems, and upstream process delays.',
      solution: 'DeltaOps collaborated with the medical center leaders to map system dependencies, integrate housekeeping, case management, and staffing data, and develop a predictive orchestration layer for real-time flow visibility.',
      results: [
        { metric: '100%', description: 'Flow huddles supported by predictive insights' },
        { metric: 'Reduced', description: 'Morning congestion through earlier bed readiness' },
        { metric: 'Faster', description: 'ED-to-inpatient transfers via upstream coordination' },
        { metric: 'System-wide', description: 'Visibility achieved across all major departments' },
      ],
      testimonial: {
        quote: "Even strong systems can run smarter. DeltaOps helped us see where flow really breaks down — and fix it before it impacts patients.",
        author: 'ED Charge Nurse'
      },
      case_id: 'DO-FC03',
    },
    {
      hospital: 'A mid-size community hospital',
      location: 'Anderson, IN',
      image: './media/images/Case_Study_3.png',
      challenge: 'The community hospital struggled to balance timely discharges and patient safety. Delayed post-acute readiness led to prolonged stays, ED backups, and underutilized home-health resources.',
      solution: 'DeltaOps implemented a predictive discharge decision tool and extended coordination into home-health planning, aligning discharge timing with post-acute capacity in real time.',
      results: [
        { metric: '20%', description: 'Reduction in readmissions' },
        { metric: '3.33 → 2.8 days', description: 'Decrease in average length of stay' },
        { metric: '25–30%', description: 'Increase in home-health utilization' },
        { metric: 'System-wide', description: 'Throughput improvement via early discharge readiness' },
      ],
      testimonial: {
        quote: "Every hour we save on discharge readiness turns into hours of capacity elsewhere in the system.",
        author: 'CEO'
      },
      case_id: 'DO-FC04',
    },
    {
      hospital: 'National Discovery Initiative',
      location: 'United States (30+ hospitals)',
      image: './media/images/Case_Study_4.png',
      challenge: 'Hospitals nationwide faced escalating staffing shortages and burnout. Despite hiring efforts, turnover remained high and operational inefficiencies persisted due to disconnected workflows.',
      solution: 'Through an NSF-supported research initiative, DeltaOps interviewed 200+ healthcare leaders to uncover root causes of staffing inefficiency and identify actionable levers for change.',
      results: [
        { metric: '200+', description: 'Leaders and managers interviewed nationwide' },
        { metric: '5', description: 'Systemic causes of staffing inefficiency identified' },
        { metric: '4', description: 'Strategic levers for change validated across institutions' },
        { metric: '1', description: 'National framework for predictive workforce coordination developed' },
      ],
      testimonial: {
        quote: "This initiative reframed staffing as a systems problem, not just a headcount issue. It gave us a clear roadmap for operational redesign.",
        author: 'COO',
      },
      case_id: 'DO-SI02'
    },
    {
      hospital: 'An Academic Medical Center',
      location: 'Honolulu, HI',
      image: './media/images/Case_Study_5.png',
      challenge: 'Observation units designed to relieve ED congestion became bottlenecks themselves. Patients waited 24–48 hours for beds while new arrivals backed up, forcing reliance on travel nurses.',
      solution: 'DeltaOps deployed its Predictive Flow Intelligence platform to forecast bottlenecks, detect process delays, and coordinate communication among ED, imaging, and inpatient teams.',
      results: [
        { metric: '18–20%', description: 'Reduction in average ED length-of-stay' },
        { metric: '25–30%', description: 'Decrease in imaging turnaround time' },
        { metric: '10–15%', description: 'Increase in ED throughput with same capacity' },
        { metric: '60%', description: 'Reduction in patients exceeding 12-hour stays' },
      ],
      testimonial: {
        quote: "It’s not a people problem — it’s a flow problem. DeltaOps helped us see where time was really being lost.",
        author: 'An Emergency Physician',
      },
      case_id: 'DO-FC05'
    }
  ];

  const keyBenefits = [
    {
      icon: <TrendingUp className="w-10 h-10 text-cyan-300" />,
      title: 'Proven Results',
      description: 'Average 25-35% improvement in key operational metrics across all implementations',
    },
    {
      icon: <Users className="w-10 h-10 text-cyan-300" />,
      title: 'Staff Satisfaction',
      description: '90%+ staff satisfaction scores with reduced burnout and improved work-life balance',
    },
    {
      icon: <Clock className="w-10 h-10 text-cyan-300" />,
      title: 'Fast Implementation',
      description: 'Go live in 4-6 weeks with full support and training from our expert team',
    },
  ];

  return (
    <div className="success-stories-page" data-testid="success-stories-page">
      <Seo
        title="Success Stories | DeltaOps AI Outcomes"
        description="Case studies from leading health systems showing how DeltaOps AI improves staffing, flow coordination, and patient outcomes."
        image="https://images.pexels.com/photos/5452201/pexels-photo-5452201.jpeg"
        imageAlt="Hospital staff walking through a clinical corridor"
      />
      {/* Hero Section */}
      <section
        className="min-h-screen flex items-center bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-900/20 text-white"
        data-testid="hero-section"
      >
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-5xl space-y-6">
            <p className="text-cyan-300 uppercase tracking-widest text-sm">Proven Impact</p>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight" data-testid="page-title">
              Success Stories
            </h1>
            <p className="text-lg text-slate-200">
              These stories show how DeltaOps AI untangles the complexity of hospital and home health coordination, keeping staff aligned and patients moving.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="btn bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-lg transition hover:-translate-y-0.5"
              >
                Request a Demo <ArrowRight className="ml-2" />
              </Link>
              <Link
                to="/operations-flow"
                className="btn border border-white/20 px-6 py-3 rounded-lg text-white hover:text-cyan-300"
              >
                Explore the Operations Flow
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 bg-[#020a17]" data-testid="benefits-section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {keyBenefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur"
                data-testid={`benefit-${index}`}
              >
                <div className="flex justify-center mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold mb-2 text-white">{benefit.title}</h3>
                <p className="text-slate-300">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-slate-900" data-testid="case-studies-section">
        <div className="container mx-auto px-4">
          <div className="space-y-20">
            {caseStudies.map((study, index) => (
              <div
                key={index}
                className="bg-white/5 rounded-2xl shadow-2xl overflow-hidden border border-white/10 backdrop-blur"
                data-testid={`case-study-${index}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                  {/* Image */}
                  <div className={`${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}>
                    <img
                      src={study.image}
                      alt={study.hospital}
                      className="w-full h-full object-cover min-h-[400px]"
                    />
                  </div>

                  {/* Content */}
                  <div className={`p-8 lg:p-12 ${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
                    <h2 className="text-3xl font-bold mb-2 text-white">{study.hospital}</h2>
                    <p className="text-cyan-300 mb-6">{study.location}</p>

                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2">Challenge</h3>
                        <p className="text-slate-300">{study.challenge}</p>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2">Solution</h3>
                        <p className="text-slate-300">{study.solution}</p>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold text-white mb-4">Results</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {study.results.map((result, resultIndex) => (
                            <div
                              key={resultIndex}
                              className="bg-white/5 p-4 rounded-lg border border-white/10"
                              data-testid={`result-${index}-${resultIndex}`}
                            >
                              <div className="text-3xl font-bold text-cyan-300 mb-1">
                                {result.metric}
                              </div>
                              <div className="text-sm text-slate-300">{result.description}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-white/5 via-cyan-900/10 to-transparent p-6 rounded-lg border-l-4 border-cyan-300">
                        <p className="text-slate-200 italic mb-4">"{study.testimonial.quote}"</p>
                        <div>
                          <p className="font-semibold text-white">{study.testimonial.author}</p>
                          <p className="text-slate-300 text-sm">{study.testimonial.title}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600/20 via-slate-900 to-slate-950 text-white" data-testid="cta-section">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Write Your Success Story?</h2>
          <p className="text-xl mb-8 text-cyan-100 max-w-2xl mx-auto">
            Join these leading healthcare organizations and transform your healtcare operations with DeltaOps AI.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center btn bg-white/10 text-white border border-white/30 hover:bg-white/20 text-lg px-8 py-4"
            data-testid="cta-button"
          >
            Schedule Your Demo <CheckCircle className="ml-2" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default SuccessStoriesPage;
